<template>
  <div class="tender-documents-panel">
    <!-- Panel Header -->
    <div class="panel-header">
      <div class="row items-center">
        <div class="col">
          <div class="panel-title">
            <q-icon name="folder" color="orange" class="q-mr-sm" />
            Tender Documents
          </div>
          <div class="panel-subtitle">
            {{ documents.length }} document{{ documents.length !== 1 ? 's' : '' }} uploaded
          </div>
        </div>
        <div class="col-auto">
          <q-btn
            color="orange"
            icon="cloud_upload"
            label="Upload Document"
            class="upload-btn"
            @click="showUploadDialog = true"
          />
        </div>
      </div>
    </div>

    <!-- Document Categories -->
    <div class="document-categories q-mb-lg">
      <q-tabs
        v-model="activeCategory"
        dense
        class="text-grey"
        active-color="orange"
        indicator-color="orange"
        align="left"
        no-caps
      >
        <q-tab name="all" label="All Documents" />
        <q-tab name="tender" label="Tender Docs" />
        <q-tab name="quotes" label="Quotes" />
        <q-tab name="contracts" label="Contracts" />
        <q-tab name="technical" label="Technical" />
        <q-tab name="correspondence" label="Correspondence" />
      </q-tabs>
    </div>

    <!-- Documents List -->
    <div v-if="filteredDocuments.length === 0" class="empty-state">
      <q-icon name="folder_open" size="4em" color="grey-5" />
      <div class="empty-title">No Documents</div>
      <div class="empty-subtitle">
        {{ activeCategory === 'all' 
          ? 'Upload documents to get started' 
          : `No documents in ${getCategoryLabel(activeCategory)} category` }}
      </div>
      <q-btn
        color="orange"
        icon="cloud_upload"
        label="Upload First Document"
        class="q-mt-md"
        @click="showUploadDialog = true"
      />
    </div>

    <div v-else class="documents-list">
      <div
        v-for="document in filteredDocuments"
        :key="document.id"
        class="document-item"
      >
        <q-card flat bordered>
          <q-card-section class="document-header">
            <div class="row items-center">
              <div class="col-auto q-mr-md">
                <q-avatar
                  :color="getFileTypeColor(document.file_type)"
                  text-color="white"
                  size="48px"
                >
                  <q-icon :name="getFileTypeIcon(document.file_type)" size="24px" />
                </q-avatar>
              </div>
              
              <div class="col">
                <div class="document-name">{{ document.name }}</div>
                <div class="document-meta">
                  <span class="file-size">{{ formatFileSize(document.file_size) }}</span>
                  <q-separator vertical class="q-mx-sm" />
                  <span class="upload-date">{{ formatDate(document.uploaded_at) }}</span>
                  <q-separator vertical class="q-mx-sm" />
                  <span class="uploaded-by">by {{ document.uploaded_by }}</span>
                </div>
                <div class="document-category">
                  <q-badge
                    :color="getCategoryColor(document.category)"
                    :label="getCategoryLabel(document.category)"
                  />
                </div>
              </div>
              
              <div class="col-auto">
                <q-btn-group>
                  <q-btn
                    flat
                    icon="visibility"
                    :disable="!canPreview(document)"
                    @click="previewDocument(document)"
                  >
                    <q-tooltip>{{ canPreview(document) ? 'Preview' : 'Preview not available' }}</q-tooltip>
                  </q-btn>
                  
                  <q-btn
                    flat
                    icon="download"
                    @click="downloadDocument(document)"
                  >
                    <q-tooltip>Download</q-tooltip>
                  </q-btn>
                  
                  <q-btn
                    flat
                    icon="more_vert"
                  >
                    <q-menu>
                      <q-list>
                        <q-item clickable @click="editDocument(document)">
                          <q-item-section avatar>
                            <q-icon name="edit" />
                          </q-item-section>
                          <q-item-section>Edit Details</q-item-section>
                        </q-item>
                        <q-item clickable @click="shareDocument(document)">
                          <q-item-section avatar>
                            <q-icon name="share" />
                          </q-item-section>
                          <q-item-section>Share</q-item-section>
                        </q-item>
                        <q-separator />
                        <q-item clickable class="text-negative" @click="confirmDelete(document)">
                          <q-item-section avatar>
                            <q-icon name="delete" />
                          </q-item-section>
                          <q-item-section>Delete</q-item-section>
                        </q-item>
                      </q-list>
                    </q-menu>
                  </q-btn>
                </q-btn-group>
              </div>
            </div>
          </q-card-section>
          
          <q-card-section v-if="document.description" class="document-description">
            <div class="description-text">{{ document.description }}</div>
          </q-card-section>
          
          <q-card-section v-if="document.tags && document.tags.length > 0" class="document-tags">
            <q-chip
              v-for="tag in document.tags"
              :key="tag"
              size="sm"
              color="blue-grey-2"
              text-color="blue-grey-8"
              icon="local_offer"
            >
              {{ tag }}
            </q-chip>
          </q-card-section>
        </q-card>
      </div>
    </div>

    <!-- Upload Dialog -->
    <q-dialog v-model="showUploadDialog" persistent>
      <q-card style="min-width: 600px">
        <q-card-section>
          <div class="text-h6">Upload Document</div>
        </q-card-section>
        
        <q-card-section>
          <q-form>
            <div class="q-gutter-md">
              <q-file
                v-model="uploadForm.file"
                label="Select File"
                outlined
                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.png,.dwg"
                max-file-size="26214400"
                @rejected="onFileRejected"
              >
                <template #prepend>
                  <q-icon name="attach_file" />
                </template>
              </q-file>
              
              <q-input
                v-model="uploadForm.name"
                label="Document Name"
                outlined
                hint="Optional: Custom name for the document"
              />
              
              <q-select
                v-model="uploadForm.category"
                :options="categoryOptions"
                label="Category"
                outlined
                emit-value
                map-options
              />
              
              <q-input
                v-model="uploadForm.description"
                label="Description"
                type="textarea"
                rows="3"
                outlined
                hint="Optional: Describe the document contents"
              />
              
              <q-select
                v-model="uploadForm.tags"
                :options="tagOptions"
                label="Tags"
                multiple
                use-chips
                use-input
                new-value-mode="add-unique"
                outlined
                hint="Add tags to help organize documents"
              />
            </div>
          </q-form>
        </q-card-section>
        
        <q-card-actions align="right">
          <q-btn flat label="Cancel" @click="cancelUpload" />
          <q-btn 
            label="Upload" 
            color="orange" 
            :loading="uploading"
            :disable="!uploadForm.file"
            @click="uploadDocument"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- Edit Document Dialog -->
    <q-dialog v-model="showEditDialog" persistent>
      <q-card style="min-width: 500px">
        <q-card-section>
          <div class="text-h6">Edit Document Details</div>
        </q-card-section>
        
        <q-card-section v-if="editingDocument">
          <q-form>
            <div class="q-gutter-md">
              <q-input
                v-model="editForm.name"
                label="Document Name"
                outlined
              />
              
              <q-select
                v-model="editForm.category"
                :options="categoryOptions"
                label="Category"
                outlined
                emit-value
                map-options
              />
              
              <q-input
                v-model="editForm.description"
                label="Description"
                type="textarea"
                rows="3"
                outlined
              />
              
              <q-select
                v-model="editForm.tags"
                :options="tagOptions"
                label="Tags"
                multiple
                use-chips
                use-input
                new-value-mode="add-unique"
                outlined
              />
            </div>
          </q-form>
        </q-card-section>
        
        <q-card-actions align="right">
          <q-btn flat label="Cancel" @click="cancelEdit" />
          <q-btn 
            label="Save Changes" 
            color="orange" 
            :loading="saving"
            @click="saveEdit"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- Document Preview Dialog -->
    <q-dialog v-model="showPreviewDialog" maximized>
      <q-card v-if="previewingDocument">
        <q-card-section class="row items-center q-pb-none">
          <div class="text-h6">{{ previewingDocument.name }}</div>
          <q-space />
          <q-btn v-close-popup icon="close" flat round dense />
        </q-card-section>
        
        <q-card-section class="q-pt-none">
          <div class="preview-container">
            <!-- PDF Preview -->
            <iframe
              v-if="previewingDocument.file_type === 'pdf'"
              :src="getDocumentUrl(previewingDocument)"
              width="100%"
              height="600px"
              frameborder="0"
            />
            
            <!-- Image Preview -->
            <img
              v-else-if="isImage(previewingDocument.file_type)"
              :src="getDocumentUrl(previewingDocument)"
              alt="Document preview"
              style="max-width: 100%; height: auto;"
            />
            
            <!-- Unsupported Preview -->
            <div v-else class="unsupported-preview">
              <q-icon name="description" size="4em" color="grey-5" />
              <div class="q-mt-md">Preview not available for this file type</div>
              <q-btn
                color="primary"
                icon="download"
                label="Download to View"
                class="q-mt-md"
                @click="downloadDocument(previewingDocument)"
              />
            </div>
          </div>
        </q-card-section>
      </q-card>
    </q-dialog>

    <!-- Delete Confirmation Dialog -->
    <q-dialog v-model="showDeleteDialog" persistent>
      <q-card>
        <q-card-section class="row items-center">
          <q-avatar icon="warning" color="negative" text-color="white" />
          <span class="q-ml-sm text-h6">Delete Document</span>
        </q-card-section>
        
        <q-card-section>
          <p>Are you sure you want to delete <strong>{{ documentToDelete?.name }}</strong>?</p>
          <p class="text-caption text-grey-7">
            This action cannot be undone. The document will be permanently removed.
          </p>
        </q-card-section>
        
        <q-card-actions align="right">
          <q-btn v-close-popup flat label="Cancel" />
          <q-btn 
            label="Delete" 
            color="negative" 
            @click="deleteDocument" 
          />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>

<script setup>
  import { ref, computed } from 'vue';
  import { useQuasar } from 'quasar';

  const props = defineProps({
    tenderId: {
      type: Number,
      required: true,
    },
    documents: {
      type: Array,
      default: () => [],
    },
  });

  defineEmits(['upload-document', 'delete-document']);

  const $q = useQuasar();

  // State
  const activeCategory = ref('all');
  const showUploadDialog = ref(false);
  const showEditDialog = ref(false);
  const showPreviewDialog = ref(false);
  const showDeleteDialog = ref(false);
  const uploading = ref(false);
  const saving = ref(false);
  const editingDocument = ref(null);
  const previewingDocument = ref(null);
  const documentToDelete = ref(null);

  // Mock documents data - in real implementation this would come from props
  const mockDocuments = ref([
    {
      id: 1,
      name: 'Tender Specifications.pdf',
      file_type: 'pdf',
      file_size: 2048576,
      category: 'tender',
      description: 'Complete technical specifications for the HVAC tender',
      tags: ['specifications', 'hvac', 'technical'],
      uploaded_at: '2024-01-15T10:30:00Z',
      uploaded_by: 'John Smith',
    },
    {
      id: 2,
      name: 'Site Plans.dwg',
      file_type: 'dwg',
      file_size: 5242880,
      category: 'technical',
      description: 'AutoCAD drawings of the site layout',
      tags: ['drawings', 'site-plan', 'autocad'],
      uploaded_at: '2024-01-16T14:20:00Z',
      uploaded_by: 'Jane Doe',
    },
    {
      id: 3,
      name: 'Contractor Quote - ABC HVAC.pdf',
      file_type: 'pdf',
      file_size: 1024000,
      category: 'quotes',
      description: 'Quote submission from ABC HVAC Services',
      tags: ['quote', 'abc-hvac'],
      uploaded_at: '2024-01-18T09:15:00Z',
      uploaded_by: 'System',
    },
  ]);

  // Form data
  const uploadForm = ref({
    file: null,
    name: '',
    category: 'tender',
    description: '',
    tags: [],
  });

  const editForm = ref({
    name: '',
    category: '',
    description: '',
    tags: [],
  });

  // Options
  const categoryOptions = [
    { label: 'Tender Documents', value: 'tender' },
    { label: 'Quotes', value: 'quotes' },
    { label: 'Contracts', value: 'contracts' },
    { label: 'Technical Documents', value: 'technical' },
    { label: 'Correspondence', value: 'correspondence' },
  ];

  const tagOptions = [
    'specifications', 'drawings', 'quote', 'contract', 'technical', 'hvac', 
    'electrical', 'plumbing', 'fire-safety', 'bms', 'site-plan', 'autocad',
  ];

  // Computed properties
  const filteredDocuments = computed(() => {
    const docs = props.documents.length > 0 ? props.documents : mockDocuments.value;
  
    if (activeCategory.value === 'all') {
      return docs;
    }
  
    return docs.filter((doc) => doc.category === activeCategory.value);
  });

  // Methods
  function getCategoryLabel(category) {
    const option = categoryOptions.find((opt) => opt.value === category);
    return option ? option.label : category;
  }

  function getCategoryColor(category) {
    const colors = {
      tender: 'primary',
      quotes: 'secondary',
      contracts: 'positive',
      technical: 'orange',
      correspondence: 'accent',
    };
    return colors[category] || 'grey';
  }

  function getFileTypeIcon(fileType) {
    const icons = {
      pdf: 'picture_as_pdf',
      doc: 'description',
      docx: 'description',
      xls: 'table_chart',
      xlsx: 'table_chart',
      jpg: 'image',
      png: 'image',
      dwg: 'architecture',
    };
    return icons[fileType] || 'description';
  }

  function getFileTypeColor(fileType) {
    const colors = {
      pdf: 'red',
      doc: 'blue',
      docx: 'blue',
      xls: 'green',
      xlsx: 'green',
      jpg: 'purple',
      png: 'purple',
      dwg: 'orange',
    };
    return colors[fileType] || 'grey';
  }

  function formatFileSize(bytes) {
    if (bytes === 0) {return '0 Bytes';}
  
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
  
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  function formatDate(dateString) {
    if (!dateString) {return '';}
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  function canPreview(document) {
    const previewableTypes = ['pdf', 'jpg', 'png'];
    return previewableTypes.includes(document.file_type);
  }

  function isImage(fileType) {
    return ['jpg', 'jpeg', 'png', 'gif'].includes(fileType);
  }

  function getDocumentUrl(document) {
    // In real implementation, return actual document URL
    return `#document-${document.id}`;
  }

  function previewDocument(document) {
    previewingDocument.value = document;
    showPreviewDialog.value = true;
  }

  function downloadDocument(document) {
    $q.notify({
      color: 'info',
      message: `Downloading ${document.name}...`,
      timeout: 2000,
    });
  }

  function editDocument(document) {
    editingDocument.value = document;
    editForm.value = {
      name: document.name,
      category: document.category,
      description: document.description || '',
      tags: [...(document.tags || [])],
    };
    showEditDialog.value = true;
  }

  function shareDocument(document) {
    $q.notify({
      color: 'info',
      message: 'Document sharing functionality will be implemented',
      timeout: 3000,
    });
  }

  function confirmDelete(document) {
    documentToDelete.value = document;
    showDeleteDialog.value = true;
  }

  function onFileRejected(rejectedEntries) {
    $q.notify({
      color: 'negative',
      message: `File rejected: ${rejectedEntries[0].failedPropValidation}`,
      timeout: 3000,
    });
  }

  async function uploadDocument() {
    uploading.value = true;
    try {
      // Simulate upload
      await new Promise((resolve) => setTimeout(resolve, 2000));
    
      // Add document to list
      const newDocument = {
        id: Date.now(),
        name: uploadForm.value.name || uploadForm.value.file.name,
        file_type: uploadForm.value.file.name.split('.').pop().toLowerCase(),
        file_size: uploadForm.value.file.size,
        category: uploadForm.value.category,
        description: uploadForm.value.description,
        tags: uploadForm.value.tags,
        uploaded_at: new Date().toISOString(),
        uploaded_by: 'Current User',
      };
    
      mockDocuments.value.unshift(newDocument);
    
      $q.notify({
        color: 'positive',
        message: 'Document uploaded successfully',
        icon: 'cloud_upload',
      });
    
      cancelUpload();
    } catch (error) {
      $q.notify({
        color: 'negative',
        message: 'Failed to upload document',
        icon: 'error',
      });
    } finally {
      uploading.value = false;
    }
  }

  function cancelUpload() {
    uploadForm.value = {
      file: null,
      name: '',
      category: 'tender',
      description: '',
      tags: [],
    };
    showUploadDialog.value = false;
  }

  async function saveEdit() {
    saving.value = true;
    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));
    
      // Update document
      const index = mockDocuments.value.findIndex((d) => d.id === editingDocument.value.id);
      if (index >= 0) {
        mockDocuments.value[index] = {
          ...mockDocuments.value[index],
          ...editForm.value,
        };
      }
    
      $q.notify({
        color: 'positive',
        message: 'Document updated successfully',
        icon: 'edit',
      });
    
      cancelEdit();
    } catch (error) {
      $q.notify({
        color: 'negative',
        message: 'Failed to update document',
        icon: 'error',
      });
    } finally {
      saving.value = false;
    }
  }

  function cancelEdit() {
    editingDocument.value = null;
    editForm.value = {
      name: '',
      category: '',
      description: '',
      tags: [],
    };
    showEditDialog.value = false;
  }

  function deleteDocument() {
    if (documentToDelete.value) {
      const index = mockDocuments.value.findIndex((d) => d.id === documentToDelete.value.id);
      if (index >= 0) {
        mockDocuments.value.splice(index, 1);
      }
    
      $q.notify({
        color: 'positive',
        message: `${documentToDelete.value.name} deleted`,
        timeout: 3000,
      });
    
      showDeleteDialog.value = false;
      documentToDelete.value = null;
    }
  }
</script>

<style lang="scss" scoped>
.tender-documents-panel {
  .panel-header {
    margin-bottom: 24px;
    padding-bottom: 16px;
    border-bottom: 2px solid #f0f0f0;
    
    .panel-title {
      font-size: 1.5rem;
      font-weight: 700;
      color: #333;
      display: flex;
      align-items: center;
      margin-bottom: 4px;
    }
    
    .panel-subtitle {
      color: #666;
      font-size: 0.9rem;
    }
    
    .upload-btn {
      font-weight: 600;
      border-radius: 8px;
    }
  }
  
  .document-categories {
    .q-tabs {
      border-bottom: 1px solid #e0e0e0;
    }
  }
  
  .empty-state {
    text-align: center;
    padding: 48px 24px;
    color: #666;
    
    .empty-title {
      font-size: 1.2rem;
      font-weight: 600;
      margin: 16px 0 8px;
    }
    
    .empty-subtitle {
      font-size: 0.9rem;
      line-height: 1.4;
    }
  }
  
  .documents-list {
    .document-item {
      margin-bottom: 16px;
      
      .document-header {
        .document-name {
          font-weight: 700;
          font-size: 1.1rem;
          color: #333;
          margin-bottom: 4px;
        }
        
        .document-meta {
          font-size: 0.85rem;
          color: #666;
          display: flex;
          align-items: center;
          margin-bottom: 8px;
        }
        
        .document-category {
          margin-bottom: 4px;
        }
      }
      
      .document-description {
        border-top: 1px solid #f0f0f0;
        padding-top: 12px;
        
        .description-text {
          color: #666;
          font-style: italic;
          line-height: 1.4;
        }
      }
      
      .document-tags {
        border-top: 1px solid #f0f0f0;
        padding-top: 12px;
        
        .q-chip {
          margin-right: 6px;
          margin-bottom: 6px;
        }
      }
    }
  }
  
  .preview-container {
    text-align: center;
    
    .unsupported-preview {
      padding: 48px;
      color: #666;
    }
  }
}
</style>